import React from 'react';
import styled from 'styled-components';
import { FaStar, FaRegStar } from 'react-icons/fa';

// Main container
const ReviewsContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

// Section header
const SectionHeader = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
`;

// Rating summary section
const RatingSummary = styled.div`
  background: #fff8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

// Overall rating display
const OverallRating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const RatingValue = styled.span`
  font-size: 36px;
  font-weight: 700;
  color: #333;
  margin-right: 16px;
`;

const RatingDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StarRating = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 8px;
`;

const StarIcon = styled(FaStar)`
  color: #ffc107;
  width: 18px;
  height: 18px;
`;

const EmptyStarIcon = styled(FaRegStar)`
  color: #ddd;
  width: 18px;
  height: 18px;
`;

const ReviewCount = styled.span`
  font-size: 14px;
  color: #666;
`;

// Rating bars container
const RatingBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const RatingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RatingLabel = styled.span`
  font-size: 14px;
  color: #666;
  width: 20px;
  text-align: right;
`;

const BarContainer = styled.div`
  flex: 1;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: #ffc107;
  width: ${props => props.width}%;
  border-radius: 4px;
`;

const BarPercentage = styled.span`
  font-size: 14px;
  color: #666;
  min-width: 30px;
  text-align: right;
`;

// Individual review card
const ReviewCard = styled.div`
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #d87ad8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin-right: 12px;
`;

const ReviewerInfo = styled.div`
  flex: 1;
`;

const ReviewerName = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 16px;
  display: block;
`;

const ReviewMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const ReviewStars = styled.div`
  display: flex;
  gap: 2px;
`;

const ReviewDate = styled.span`
  font-size: 14px;
  color: #666;
`;

const ReviewText = styled.p`
  font-size: 16px;
  color: #444;
  line-height: 1.5;
  margin: 0;
`;

// Load more button
const LoadMoreButton = styled.button`
  background: transparent;
  border: 1px solid #ff6b6b;
  color: #ff6b6b;
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #fff0f0;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.2);
  }
`;

// Component
const ReviewsAndRatings = () => {
  // Mock data for ratings
  const ratingData = [
    { stars: 5, percentage: 75, count: 50 },
    { stars: 4, percentage: 20, count: 40 },
    { stars: 3, percentage: 3, count: 30 },
    { stars: 2, percentage: 1, count: 20 },
    { stars: 1, percentage: 1, count: 10 },
  ];
  
  // Mock reviews
  const reviews = [
    {
      id: 1,
      name: 'Anonymous User',
      rating: 5,
      date: '2 weeks ago',
      text: 'Excellent experience! The counselor was very understanding and provided practical strategies that really helped me manage my anxiety. I felt heard and supported throughout the session.'
    },
    {
      id: 2,
      name: 'Anonymous User',
      rating: 5,
      date: '2 weeks ago',
      text: 'Excellent experience! The counselor was very understanding and provided practical strategies that really helped me manage my anxiety. I felt heard and supported throughout the session.'
    },
    {
      id: 3,
      name: 'Anonymous User',
      rating: 5,
      date: '2 weeks ago',
      text: 'Excellent experience! The counselor was very understanding and provided practical strategies that really helped me manage my anxiety. I felt heard and supported throughout the session.'
    },
    {
      id: 4,
      name: 'Anonymous User',
      rating: 5,
      date: '2 weeks ago',
      text: 'Excellent experience! The counselor was very understanding and provided practical strategies that really helped me manage my anxiety. I felt heard and supported throughout the session.'
    },
  ];

  return (
    <ReviewsContainer>
      <SectionHeader>Reviews & Ratings</SectionHeader>
      
      <RatingSummary>
        <OverallRating>
          <RatingValue>4.8</RatingValue>
          <RatingDetails>
            <StarRating>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} />
              ))}
            </StarRating>
            <ReviewCount>24 reviews</ReviewCount>
          </RatingDetails>
        </OverallRating>
        
        <RatingBars>
          {ratingData.map((item, index) => (
            <RatingBar key={index}>
              <RatingLabel>{item.stars}</RatingLabel>
              <BarContainer>
                <BarFill width={item.percentage} />
              </BarContainer>
              <BarPercentage>{item.percentage}%</BarPercentage>
            </RatingBar>
          ))}
        </RatingBars>
      </RatingSummary>
      
      {reviews.map(review => (
        <ReviewCard key={review.id}>
          <ReviewHeader>
            <Avatar>A</Avatar>
            <ReviewerInfo>
              <ReviewerName>{review.name}</ReviewerName>
              <ReviewMetadata>
                <ReviewStars>
                  {[...Array(review.rating)].map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                  {[...Array(5 - review.rating)].map((_, i) => (
                    <EmptyStarIcon key={i} />
                  ))}
                </ReviewStars>
                <ReviewDate>{review.date}</ReviewDate>
              </ReviewMetadata>
            </ReviewerInfo>
          </ReviewHeader>
          <ReviewText>{review.text}</ReviewText>
        </ReviewCard>
      ))}
      
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <LoadMoreButton>Load More Reviews</LoadMoreButton>
      </div>
    </ReviewsContainer>
  );
};

export default ReviewsAndRatings;